import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import type { ObservabilityUserContext } from './types';

const TAG = '[observability/appInsights]';

let appInsights: ApplicationInsights | null = null;
let initAttempted = false;

export function initAppInsights(): ApplicationInsights | null {
    if (initAttempted) return appInsights;
    initAttempted = true;

    const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
    if (!connectionString) {
        console.info(`${TAG} VITE_APPINSIGHTS_CONNECTION_STRING not set – client telemetry disabled`);
        return null;
    }

    appInsights = new ApplicationInsights({
        config: {
            connectionString,
            enableAutoRouteTracking: false, // page views are driven explicitly by RouteChangeTracker instead
            enableCorsCorrelation: true, // correlate client fetches to our own /api/* with server-side traces
            disableFetchTracking: false, // auto-tracks every fetch() call as Dependency telemetry (client-perceived API durability)
            correlationHeaderExcludedDomains: [
                'graph.microsoft.com',
                '*.sharepoint.com',
                'login.microsoftonline.com',
                'login.windows.net',
                'www.googletagmanager.com',
                'www.clarity.ms',
                // WASM/asset CDNs (onnxruntime-web model files, dotlottie player fallback) —
                // their CORS policies reject the injected request-id/traceparent headers.
                'cdn.jsdelivr.net',
                'unpkg.com',
            ],
        },
    });
    appInsights.loadAppInsights();
    return appInsights;
}

export function setAppInsightsUserContext(ctx: ObservabilityUserContext): void {
    appInsights?.setAuthenticatedUserContext(ctx.userId, ctx.tenantId, false);
}

export function clearAppInsightsUserContext(): void {
    appInsights?.clearAuthenticatedUserContext();
}

export function trackAppInsightsPageView(name: string, uri?: string): void {
    appInsights?.trackPageView({ name, uri });
}

export function trackAppInsightsEvent(
    name: string,
    properties?: Record<string, string>,
    measurements?: Record<string, number>,
): void {
    appInsights?.trackEvent({ name, properties, measurements });
}

export function trackAppInsightsException(error: unknown, properties?: Record<string, string>): void {
    const exception = error instanceof Error ? error : new Error(String(error));
    appInsights?.trackException({ exception, properties });
}
